import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Activity, Search, FileText, Layers, ShieldCheck, Users, CreditCard, Filter, X } from "lucide-react";

const CATEGORIES = [
  { key: "all", label: "All", icon: Activity, prefix: null },
  { key: "document", label: "Documents", icon: FileText, prefix: "document" },
  { key: "template", label: "Templates", icon: Layers, prefix: "template" },
  { key: "standards", label: "Standards", icon: ShieldCheck, prefix: "standards" },
  { key: "team", label: "Team", icon: Users, prefix: "team" },
  { key: "subscription", label: "Billing", icon: CreditCard, prefix: "subscription" },
];

const RANGES = [
  { key: "24h", label: "Last 24 hours", hours: 24 },
  { key: "7d", label: "Last 7 days", hours: 24 * 7 },
  { key: "30d", label: "Last 30 days", hours: 24 * 30 },
  { key: "90d", label: "Last 90 days", hours: 24 * 90 },
  { key: "all", label: "All time", hours: 0 },
];

const ACTION_LABELS = {
  "workspace.created": "created the workspace",
  "template.created": "added a template",
  "template.duplicated": "duplicated a template",
  "document.generated": "generated a document",
  "document.exported": "exported a document as DOCX",
  "document.finalized": "marked a document as final",
  "document.archived": "archived a document",
  "document.deleted": "deleted a document",
  "document.metadata_updated": "updated document metadata",
  "standards.updated": "updated workspace standards",
  "folder.created": "created a folder",
  "subscription.activated": "activated a subscription",
  "team.invited": "invited a teammate",
  "team.member_added": "added a member",
  "team.member_removed": "removed a member",
  "team.role_changed": "changed a member's role",
  "team.invite_accepted": "accepted a team invite",
};

function relative(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function dayKey(iso) {
  const d = new Date(iso);
  return d.toDateString();
}

export default function ActivityPage() {
  const { activeWorkspace, canAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [facets, setFacets] = useState({ users: [], actions: [], entity_types: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [userId, setUserId] = useState("");
  const [range, setRange] = useState("30d");

  const load = async () => {
    if (!activeWorkspace) return;
    setLoading(true);
    const params = new URLSearchParams();
    const cat = CATEGORIES.find((c) => c.key === category);
    if (cat?.prefix) params.set("action", cat.prefix);
    if (userId) params.set("user_id", userId);
    if (q.trim()) params.set("q", q.trim());
    const r = RANGES.find((x) => x.key === range);
    if (r && r.hours > 0) {
      const since = new Date(Date.now() - r.hours * 3600 * 1000).toISOString();
      params.set("since", since);
    }
    try {
      const [{ data: rows }, { data: fac }] = await Promise.all([
        api.get(`/workspaces/${activeWorkspace.id}/activity?${params.toString()}`),
        api.get(`/workspaces/${activeWorkspace.id}/activity/facets`),
      ]);
      setLogs(rows || []);
      setFacets(fac || { users: [], actions: [], entity_types: [] });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspace, category, userId, range]);
  useEffect(() => {
    if (!activeWorkspace) return;
    const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */
  }, [q]);

  const grouped = useMemo(() => {
    const g = new Map();
    for (const l of logs) {
      const k = dayKey(l.created_at);
      if (!g.has(k)) g.set(k, []);
      g.get(k).push(l);
    }
    return Array.from(g.entries());
  }, [logs]);

  const clearFilters = () => { setQ(""); setCategory("all"); setUserId(""); setRange("30d"); };
  const anyActive = q || category !== "all" || userId || range !== "30d";

  return (
    <div className="max-w-5xl mx-auto p-8" data-testid="activity-page">
      <div className="mb-6 flex flex-wrap justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Activity timeline</h1>
          <p className="text-sm text-slate-400">{canAdmin ? "Audit every action taken in this workspace." : "See what has been happening in this workspace."}</p>
        </div>
      </div>

      <div className="fx-panel p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="activity-filters">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] bg-[#050816] border border-[#1E293B] rounded-lg px-3 py-1.5">
          <Search size={14} className="text-slate-500" />
          <input placeholder="Search document, template or member…" value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 bg-transparent text-sm text-slate-200 outline-none" data-testid="activity-search" />
        </div>
        <select value={userId} onChange={(e) => setUserId(e.target.value)} className="bg-[#050816] border border-[#1E293B] rounded-lg px-3 py-1.5 text-sm text-slate-200" data-testid="activity-user">
          <option value="">Any member</option>
          {facets.users.map((u) => (
            <option key={u.id} value={u.id}>{`${`${u.first_name || ""} ${u.last_name || ""}`.trim()} — ${u.email}`}</option>
          ))}
        </select>
        <select value={range} onChange={(e) => setRange(e.target.value)} className="bg-[#050816] border border-[#1E293B] rounded-lg px-3 py-1.5 text-sm text-slate-200" data-testid="activity-range">
          {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        {anyActive && <button onClick={clearFilters} className="text-slate-500 hover:text-cyan-300 text-xs inline-flex items-center gap-1" data-testid="activity-clear"><X size={12} /> Clear</button>}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {CATEGORIES.map((c) => {
          const I = c.icon;
          return (
            <button key={c.key} onClick={() => setCategory(c.key)} className={`text-xs px-3 py-1.5 rounded-lg border inline-flex items-center gap-1.5 ${category === c.key ? "border-cyan-400/60 bg-cyan-500/10 text-cyan-200" : "border-[#1E293B] text-slate-400 hover:text-slate-200 hover:border-slate-600"}`} data-testid={`activity-cat-${c.key}`}>
              <I size={12} /> {c.label}
            </button>
          );
        })}
      </div>

      <div className="fx-panel">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
        ) : grouped.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500 inline-flex flex-col items-center gap-2 w-full">
            <Filter size={20} className="text-slate-600" />
            No activity for the current filters.
          </div>
        ) : (
          grouped.map(([day, items]) => (
            <div key={day}>
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500 px-4 pt-4 pb-2 border-b border-[#1E293B]" data-testid={`day-${day.replace(/\s+/g, '-')}`}>{day}</div>
              <div className="divide-y divide-[#1E293B]">
                {items.map((l) => (
                  <div key={l.id} className="p-4 flex items-start gap-3" data-testid={`activity-${l.id}`}>
                    <div className="w-8 h-8 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
                      <Activity size={14} className="text-cyan-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-200">
                        <span className="font-medium">{l.user?.first_name || l.user?.email || "Someone"}</span>{" "}
                        {ACTION_LABELS[l.action] || l.action.replace(/[._]/g, ' ')}
                        {(l.metadata?.name || l.metadata?.title) && (
                          <span className="text-slate-400"> — <span className="text-slate-200">{l.metadata.name || l.metadata.title}</span></span>
                        )}
                        {l.metadata?.email && (
                          <span className="text-slate-400"> — <span className="text-slate-200">{l.metadata.email}</span></span>
                        )}
                        {l.metadata?.new_role && (
                          <span className="text-slate-400"> to <span className="text-slate-200">{l.metadata.new_role}</span></span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 mono mt-0.5">{relative(l.created_at)} · {new Date(l.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
